import {
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  PutObjectCommand,
  S3Client,
  UploadPartCommand,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { v4 as uuidv4 } from "uuid";

const MB = 1024 * 1024;

/** Files up to this size are uploaded in one request, larger files in parts of this size */
const PART_SIZE = 8 * MB;
const PART_CONCURRENCY = 3;
const MAX_PARTS = 10000;

const MAX_ATTEMPTS = 6;
const RETRY_DELAY = 2000;
/** A request without progress for this long is aborted and retried */
const STALE_TIMEOUT = 60000;
const URL_EXPIRES = 3600;

/**
 * Upload config from `/upload/init` (`s3`). The files are uploaded to the cf (EU) upload bucket through its CloudFront
 * distribution (`cf.url`), like the app does: uploaders far from the bucket connect to a nearby edge.
 */
export type S3UploadConfig = {
  bucket: string;
  region: string;
  cf: { bucket: string; region: string; url: string };
  credentials: { AccessKeyId: string; SecretAccessKey: string; SessionToken: string; Expiration: string };
};

type Response = { status: number; body: string; etag: string | null };

class RequestError extends Error {
  status: number;
  body: string;

  constructor(message: string, status: number, body = "") {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Difference between the clock of S3 and the browser (in case the clock of the browser is off) */
let clockOffset = 0;

/**
 * Uploads a file to the S3 upload bucket with presigned requests through CloudFront, the upload worker processes it.
 * Retries failed and stale requests.
 */
export default class S3FileUpload {
  private file: File;
  private config: S3UploadConfig;
  private client: S3Client;
  private metadata: Record<string, string>;
  private key = uuidv4();

  private cancelled = false;
  private requests = new Set<XMLHttpRequest>();
  private partsLoaded = new Map<number, number>();

  private _onProgress: (loaded: number, total: number) => void = () => {};
  private _onRetry: (info: object) => void = () => {};

  /**
   * @param uploadKey Key of the upload (`/upload/init`) the file belongs to
   */
  constructor(file: File, config: S3UploadConfig, uploadKey: string) {
    this.file = file;
    this.config = config;
    this.metadata = {
      uploadkey: uploadKey,
      filename: S3FileUpload.encodeMetadata(JSON.stringify(file.name)),
    };
    //Only used for signing, the requests are sent with XMLHttpRequest
    this.client = new S3Client({
      region: config.cf.region,
      credentials: {
        accessKeyId: config.credentials.AccessKeyId,
        secretAccessKey: config.credentials.SecretAccessKey,
        sessionToken: config.credentials.SessionToken,
      },
    });
  }

  /**
   * S3 metadata has to be ASCII: escape other characters in the JSON (json_decode in the worker restores them)
   */
  static encodeMetadata(json: string) {
    return json.replace(/[\u007f-￿]/g, (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
  }

  onProgress(callback: (loaded: number, total: number) => void) {
    this._onProgress = callback;
  }

  onRetry(callback: (info: object) => void) {
    this._onRetry = callback;
  }

  get partSize() {
    return Math.max(PART_SIZE, Math.ceil(this.file.size / MAX_PARTS));
  }

  async start() {
    if (this.file.size <= this.partSize) {
      const command = new PutObjectCommand({ Bucket: this.config.cf.bucket, Key: this.key, Metadata: this.metadata });
      await this.request("PUT", command, this.file, 1);
    } else {
      await this.uploadMultipart();
    }
  }

  cancel() {
    this.cancelled = true;
    this.requests.forEach((xhr) => xhr.abort());
  }

  private async uploadMultipart() {
    const created = await this.request(
      "POST",
      new CreateMultipartUploadCommand({ Bucket: this.config.cf.bucket, Key: this.key, Metadata: this.metadata }),
    );
    const uploadId = S3FileUpload.xmlValue(created.body, "UploadId");
    if (!uploadId) {
      throw new RequestError("No UploadId in CreateMultipartUpload response", created.status, created.body);
    }

    //Parts of a failed upload are cleaned up by the lifecycle rule of the bucket (the upload credentials can only put)
    const partCount = Math.ceil(this.file.size / this.partSize);
    const parts: Array<{ partNumber: number; etag: string }> = [];
    let nextPart = 1;

    //Upload the parts with a few at the same time
    const uploadParts = async () => {
      while (nextPart <= partCount && !this.cancelled) {
        const partNumber = nextPart++;
        const body = this.file.slice((partNumber - 1) * this.partSize, partNumber * this.partSize);
        const command = new UploadPartCommand({
          Bucket: this.config.cf.bucket,
          Key: this.key,
          UploadId: uploadId,
          PartNumber: partNumber,
        });
        const { etag } = await this.request("PUT", command, body, partNumber);
        if (!etag) {
          throw new RequestError("No ETag in UploadPart response (not exposed by CORS?)", 200);
        }
        parts.push({ partNumber, etag });
      }
    };
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, partCount) }, uploadParts));
    this.throwIfCancelled();

    parts.sort((a, b) => a.partNumber - b.partNumber);
    const xml = `<CompleteMultipartUpload>${parts
      .map((part) => `<Part><PartNumber>${part.partNumber}</PartNumber><ETag>${part.etag}</ETag></Part>`)
      .join("")}</CompleteMultipartUpload>`;
    const completed = await this.request(
      "POST",
      new CompleteMultipartUploadCommand({ Bucket: this.config.cf.bucket, Key: this.key, UploadId: uploadId }),
      xml,
    );
    //Complete can fail after a 200 response, the error is in the body
    if (completed.body.includes("<Error>")) {
      const code = S3FileUpload.xmlValue(completed.body, "Code") || "CompleteMultipartUpload failed";
      throw new RequestError(code, 200, completed.body);
    }
  }

  /**
   * Sends a presigned request through CloudFront and retries it when it fails or stalls
   * @param part Part number for the progress (0 for requests without file data)
   */
  private async request(
    method: "PUT" | "POST",
    command: PutObjectCommand | CreateMultipartUploadCommand | UploadPartCommand | CompleteMultipartUploadCommand,
    body?: Blob | string,
    part = 0,
  ): Promise<Response> {
    const size = body instanceof Blob ? body.size : 0;

    for (let attempt = 1; ; attempt++) {
      this.throwIfCancelled();

      try {
        const url = await this.presign(command);
        const response = await this.send(method, url, body, (loaded) => {
          if (part > 0) {
            this.setPartLoaded(part, Math.min(loaded, size));
          }
        });
        if (part > 0) {
          this.setPartLoaded(part, size);
        }
        return response;
      } catch (error: any) {
        this.throwIfCancelled();
        if (part > 0) {
          this.setPartLoaded(part, 0);
        }

        const skewed = this.correctClock(error);
        const retryable = skewed || S3FileUpload.isRetryable(error);
        this._onRetry({
          type: error?.message,
          status: error?.status,
          shouldRetry: retryable,
          attempt,
          maxAttempts: MAX_ATTEMPTS,
        });
        if (!retryable || attempt >= MAX_ATTEMPTS) {
          throw error;
        }
        if (!skewed) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * 2 ** (attempt - 1)));
        }
      }
    }
  }

  /**
   * Presigned url (all headers, incl. the metadata, in the query string) with the host of the CloudFront distribution
   */
  private async presign(command: Parameters<typeof getSignedUrl>[1]) {
    const signed = new URL(
      await getSignedUrl(this.client, command, {
        expiresIn: URL_EXPIRES,
        signingDate: new Date(Date.now() + clockOffset),
        unhoistableHeaders: new Set(),
      }),
    );

    if (this.config.cf.url) {
      const cf = new URL(this.config.cf.url);
      signed.protocol = cf.protocol;
      signed.host = cf.host;
    }
    return signed.toString();
  }

  private send(method: string, url: string, body: Blob | string | undefined, onProgress: (loaded: number) => void) {
    return new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.requests.add(xhr);

      let staleTimer: ReturnType<typeof setTimeout>;
      const resetStaleTimer = () => {
        clearTimeout(staleTimer);
        staleTimer = setTimeout(() => {
          xhr.abort();
          reject(new RequestError("stale", 0));
        }, STALE_TIMEOUT);
      };
      const done = () => {
        clearTimeout(staleTimer);
        this.requests.delete(xhr);
      };

      xhr.upload.addEventListener("progress", (event) => {
        resetStaleTimer();
        onProgress(event.loaded);
      });
      xhr.addEventListener("load", () => {
        done();
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, body: xhr.responseText, etag: xhr.getResponseHeader("ETag") });
        } else {
          const code = S3FileUpload.xmlValue(xhr.responseText, "Code");
          reject(new RequestError(code || `HTTP ${xhr.status}`, xhr.status, xhr.responseText));
        }
      });
      xhr.addEventListener("error", () => {
        done();
        reject(new RequestError("network error", 0));
      });
      xhr.addEventListener("abort", () => {
        done();
        reject(new RequestError("aborted", 0));
      });

      xhr.open(method, url);
      resetStaleTimer();
      xhr.send(body ?? null);
    });
  }

  /**
   * When the request failed because the clock of the browser is off, corrects the clock with the time of S3
   * @returns If the clock was corrected (the request can be retried right away)
   */
  private correctClock(error: any) {
    if (error?.status !== 403) {
      return false;
    }
    const serverTime = Date.parse(S3FileUpload.xmlValue(error.body, "ServerTime") || "");
    if (Number.isNaN(serverTime) || Math.abs(serverTime - (Date.now() + clockOffset)) < 5 * 60 * 1000) {
      return false;
    }
    clockOffset = serverTime - Date.now();
    return true;
  }

  private setPartLoaded(part: number, loaded: number) {
    this.partsLoaded.set(part, loaded);
    let total = 0;
    this.partsLoaded.forEach((partLoaded) => (total += partLoaded));
    this._onProgress(total, this.file.size);
  }

  private throwIfCancelled() {
    if (this.cancelled) {
      throw new Error("Upload cancelled");
    }
  }

  static xmlValue(xml: string, tag: string) {
    return xml.match(new RegExp(`<${tag}>([^<]*)</${tag}>`))?.[1];
  }

  /**
   * Network errors, aborted (stale) requests, throttling and server errors are retried
   */
  static isRetryable(error: any) {
    const status = error?.status;
    return !status || status >= 500 || status === 429 || status === 408;
  }
}
